/**
 * Decision Receipt — ONE classifier every consumer reads a decision row through (MAR-64).
 *
 * ═══════════════════════════════════════════════════════════════════════════════════════
 * PR #95 made decision rows carry `judgment` / `disposition` / `eligibility` /
 * `decisionFailure`. Nothing read them. Every consumer kept branching on the legacy mirror
 * (`help → engage`, `wait | escalate | failure → skip`), so a Bedrock timeout read as
 * "held off", a cooldown-blocked help read as "sent", and three counters spent a shopper's
 * contact budget on messages that never left the building.
 * ═══════════════════════════════════════════════════════════════════════════════════════
 *
 * This module is the choke point. A consumer calls `classifyDecision(row)` and switches on
 * `class`; nobody re-derives a class from `action`/`status`, because the interesting cases —
 * a failure row that looks like `skip/dispatched`, a deferral that later executed, an
 * authority hold — are exactly the ones a hand-rolled mapping gets wrong one consumer at a
 * time.
 *
 * ── Dependency-free, on purpose ──────────────────────────────────────────────────────────
 * Requires ONLY `./decision-vocabulary` and `./outcome-vocabulary` (and, through the former,
 * `./block-reasons`). No AWS client, no zod. It is read by the web app's type declarations,
 * the activity feed, the eval harness and the gateway's EMF lines; a vocabulary that builds a
 * client at load costs every one of those consumers a client (see `block-reasons.js` for the
 * time that happened).
 *
 * ── The classes, in precedence order ─────────────────────────────────────────────────────
 *
 *   decision_failed   no judgment was reached (context / model / parse / routing broke).
 *                     Checked BEFORE the normalizer: a failure row is written
 *                     `action:'skip', status:'dispatched', judgment:null`, and
 *                     `normalizeDecisionRead` would call that `legacy_ambiguous`.
 *   legacy_ambiguous  a pre-D2 `skip`/`defer` with no structured provenance. Displayable,
 *                     never restraint, never a contact.
 *   wait              Marva chose silence. The restraint arm.
 *   escalate          Marva judged the merchant is needed. Nothing sent to the shopper.
 *   help_shadowed     shadow mode — reasoned, never eligible to send. Checked BEFORE the
 *                     failure test: a shadow draft that failed (`draftFailed: true`) was never
 *                     going to be sent, so it is not an execution failure.
 *   execution_failed  a help that broke between judgment and the shopper — `failed` status,
 *                     a post-judgment `decisionFailure`, a refuted delivery proof, or a
 *                     dropped socket frame. `failure.phase` is compose | send | delivery.
 *   help_blocked      a help policy withheld — suppressed, a blocked disposition, or an
 *                     authority hold (`escalated` on a help; `pendingApproval: true`).
 *                     Carries `deferredUntil` when it had been deferred first.
 *   help_withdrawn    cancelled or expired before sending.
 *   help_deferred     scheduled for later (agent timing or quiet hours).
 *   help_executed     a send site confirmed it (`executed` / `delivered`).
 *   help_in_flight    handed off, nothing back yet (`dispatched` / `pending_delivery`).
 *                     An unknown (or `decided`) status lands here flagged
 *                     `unrecognisedStatus` with `reachedCustomer: false`; a MISSING status
 *                     (legacy row) is still a contact, matching `reachedCustomer()`.
 *                     ⚠️ Only the MISSING case matches it: outcome-vocabulary's
 *                     `reachedCustomer()` returns TRUE for an unknown string ('bogus'), where
 *                     this returns false. Unreachable today — `DecisionSchema.status` is a zod
 *                     enum and `updateDecisionStatus` throws on a status it cannot write.
 *
 * @module decision-receipt
 */

'use strict';

const {
  BLOCKED_REASONS,
  DECISION_FAILURE_CODES,
  DEDUPLICATION_REASONS,
  FEASIBILITY_REASONS,
  INELIGIBLE_REASONS,
  LIFECYCLE_REASONS,
  PRE_JUDGMENT_BLOCK_REASONS,
  SELF_RESTRAINT_REASONS,
  AUTHORITY_REASONS,
  MODE_REASONS,
  LEGACY_AMBIGUOUS,
  normalizeDecisionRead,
  countsTowardRestraint
} = require('./decision-vocabulary');
const { BLOCK_REASONS } = require('./block-reasons');
const { DELIVERY_NOT_REACHED } = require('./outcome-vocabulary');
// MAR-411: what a product help selected, or why it was withheld. Dependency-free.
const { describeSelection } = require('./selection-receipt');

// ── Statuses ───────────────────────────────────────────────────────────────────────────

/**
 * Every status a decision row can hold. THE list — `DecisionSchema.status` and the activity
 * feed read it from here rather than keeping copies that drift (the enum trailed the writers
 * by three values for months).
 */
const DECISION_STATUSES = Object.freeze([
  'scheduled', 'dispatched', 'executed', 'cancelled', 'expired', 'failed',
  'suppressed', 'shadowed', 'escalated', 'pending_delivery', 'delivered',
  'decided'
]);

// ── Classes ────────────────────────────────────────────────────────────────────────────

const DECISION_CLASSES = Object.freeze([
  'decision_failed',
  'legacy_ambiguous',
  'wait',
  'escalate',
  'execution_failed',
  'help_blocked',
  'help_withdrawn',
  'help_deferred',
  'help_shadowed',
  'help_executed',
  'help_in_flight'
]);

/**
 * The classes a CONTACT can belong to — a message a shopper was, or is being, handed. Class
 * membership is necessary but not sufficient: a `help_in_flight` with an unrecognised status
 * is not a contact. Read contacts through `isContact` / `reachedCustomer`, never this list.
 */
const CONTACT_CLASSES = Object.freeze(['help_executed', 'help_in_flight']);

/** Where a proposed help broke on its way to the shopper. */
const EXECUTION_FAILURE_PHASES = Object.freeze(['compose', 'send', 'delivery']);

/** Post-judgment failure codes that mean the COPY never existed, rather than the send broke. */
const COMPOSE_FAILURE_CODES = Object.freeze([
  DECISION_FAILURE_CODES.EMPTY_MESSAGE,
  DECISION_FAILURE_CODES.CONTEXT_ARTIFACT_UNAVAILABLE
]);

/**
 * The phase of an execution failure. Exported so the gateway's `ExecutionFailed{phase}` EMF
 * line and the classifier can never disagree about the same row.
 *
 * @param {{decisionFailure?: object, deliveryProof?: string, deliveryStatus?: string}} row
 * @returns {'compose'|'send'|'delivery'}
 */
function executionFailurePhase(row) {
  const failure = row && row.decisionFailure;
  if (failure && (failure.stage === 'composition' || COMPOSE_FAILURE_CODES.includes(failure.code))) {
    return 'compose';
  }
  if (row && (row.deliveryProof === DELIVERY_NOT_REACHED || row.deliveryStatus === 'failed')) {
    return 'delivery';
  }
  return 'send';
}

function hasJudgment(row) {
  return Boolean(row && row.judgment && typeof row.judgment === 'object' && row.judgment.kind);
}

function judgmentSourceOf(row, normalized) {
  if (hasJudgment(row)) return row.judgment.source || 'agent';
  if (normalized && normalized.source === 'legacy' && !normalized.ambiguous) return 'legacy';
  return null;
}

/**
 * Classify one decision row. Pure; writes nothing; never throws on a malformed row.
 *
 * @param {object} row - a decision row, canonical or legacy
 * @returns {{
 *   class: string,
 *   judgment: 'help'|'wait'|'escalate'|null,
 *   judgmentSource: string|null,
 *   disposition: object|null,
 *   reasonCode: string|null,
 *   until: string|null,
 *   deferredUntil: string|null,
 *   failure: {stage: string|null, code: string|null, phase?: string}|null,
 *   reachedCustomer: boolean,
 *   countsTowardRestraint: boolean,
 *   pendingApproval: boolean,
 *   draftFailed?: boolean,
 *   unrecognisedStatus: boolean,
 *   legacy: boolean
 * }}
 */
function classifyDecision(row) {
  const safe = row && typeof row === 'object' ? row : {};
  const status = typeof safe.status === 'string' ? safe.status : null;

  const base = {
    judgment: null,
    judgmentSource: null,
    disposition: null,
    reasonCode: null,
    until: null,
    deferredUntil: null,
    failure: null,
    reachedCustomer: false,
    countsTowardRestraint: false,
    pendingApproval: false,
    unrecognisedStatus: false,
    legacy: false
  };

  // 1. A FAILURE IS NOT A JUDGMENT — checked before the normalizer, which would read the
  //    failure row's `skip/dispatched` mirror as an ambiguous legacy skip.
  if (safe.decisionFailure && !hasJudgment(safe)) {
    return {
      ...base,
      class: 'decision_failed',
      failure: {
        stage: safe.decisionFailure.stage || null,
        code: safe.decisionFailure.code || null
      }
    };
  }

  // 1b. A PRE-D2 SHADOW PROPOSAL. `logShadowDecision` (shadow-interceptor, called by
  //     shopper-email) writes `mode: 'shadow'` with `action` as the proposed send's PAYLOAD
  //     object (`{emailSubject, emailBody, …}`), no judgment and no status. That row IS a help
  //     that shadow mode held — the object is the help — but the normalizer only knows the
  //     string mirror and would call it ambiguous, which the Journal then renders as "didn't
  //     reach out". Recognised here, at the choke point, so no surface patches it locally.
  if (isLegacyShadowProposal(safe)) {
    return {
      ...base,
      class: 'help_shadowed',
      judgment: 'help',
      judgmentSource: 'legacy',
      legacy: true,
      countsTowardRestraint: restraintEligible(safe)
    };
  }

  const normalized = normalizeDecisionRead(safe);
  const legacy = normalized.source === 'legacy';

  // 2. A legacy skip we refuse to guess about.
  if (normalized.ambiguous || !normalized.judgment) {
    return { ...base, class: 'legacy_ambiguous', legacy: true };
  }

  const judgment = normalized.judgment;
  const disposition = normalized.disposition || null;
  const common = {
    ...base,
    judgment,
    judgmentSource: judgmentSourceOf(safe, normalized),
    disposition,
    legacy,
    countsTowardRestraint: restraintEligible(safe)
  };

  // 3–4. The two judgments that proposed nothing to a channel.
  if (judgment === 'wait') return { ...common, class: 'wait' };
  if (judgment === 'escalate') return { ...common, class: 'escalate' };

  // ── From here, a HELP. What happened to it? ─────────────────────────────────────────
  // Every class but `help_deferred` reads the disposition's `until` as the deferral this help
  // had carried: `dispositionFor` keeps the prior hold's `until` on every transition it
  // builds, and the scheduled-executor's expired / cancelled / failed flips write no
  // disposition at all — leaving the `deferred` one, whose `until` is that same hold.
  const deferredUntil = disposition ? (disposition.until || null) : null;

  // 5. Shadow — reasoned, never eligible to act. BEFORE the failure test: a shadow draft
  //    that failed (`stampShadowedFailure` leaves `status: 'shadowed'` + a `decisionFailure`)
  //    was never going to be sent, so "tried to send, didn't go through" would be false. The
  //    failure rides on the receipt as `draftFailed`; the class stays shadowed.
  if (status === 'shadowed') {
    return {
      ...common,
      class: 'help_shadowed',
      ...(safe.decisionFailure
        ? {
            draftFailed: true,
            failure: {
              stage: safe.decisionFailure.stage || null,
              code: safe.decisionFailure.code || null,
              phase: executionFailurePhase(safe)
            }
          }
        : {})
    };
  }

  // 6. Broke between the judgment and the shopper.
  if (
    status === 'failed' ||
    safe.decisionFailure ||
    safe.deliveryProof === DELIVERY_NOT_REACHED ||
    safe.deliveryStatus === 'failed'
  ) {
    return {
      ...common,
      class: 'execution_failed',
      deferredUntil,
      failure: {
        stage: (safe.decisionFailure && safe.decisionFailure.stage) || null,
        code: (safe.decisionFailure && safe.decisionFailure.code) || null,
        phase: executionFailurePhase(safe)
      }
    };
  }

  // 7. Policy withheld it — or it is waiting on the merchant.
  const pendingApproval = status === 'escalated';
  if (status === 'suppressed' || pendingApproval || (disposition && disposition.kind === 'blocked')) {
    const reasonCode = (disposition && disposition.reasonCode) ||
      safe.suppressionReason ||
      (pendingApproval ? LIFECYCLE_REASONS.ESCALATION_PENDING : null);
    return {
      ...common,
      class: 'help_blocked',
      reasonCode,
      deferredUntil,
      pendingApproval
    };
  }

  // 8. Withdrawn before it went.
  if (status === 'cancelled' || status === 'expired') {
    return {
      ...common,
      class: 'help_withdrawn',
      reasonCode: status === 'cancelled' ? (safe.cancellationReason || 'cancelled') : 'expired',
      deferredUntil
    };
  }

  // 9. Moved in time.
  const deferredDisposition = disposition && disposition.kind === 'deferred';
  if (status === 'scheduled' || (deferredDisposition && !CONTACT_STATUSES.includes(status))) {
    return {
      ...common,
      class: 'help_deferred',
      reasonCode: (deferredDisposition && disposition.reasonCode) || LIFECYCLE_REASONS.SCHEDULED,
      until: (deferredDisposition && disposition.until) || safe.gsi3sk || null
    };
  }

  // 10–11. Sent, or on its way.
  if (status === 'executed' || status === 'delivered') {
    return { ...common, class: 'help_executed', deferredUntil, reachedCustomer: true };
  }
  // A help with a status that is not a send state — an unknown value, or `decided` (the
  // terminal status of a WAIT, which a help should never carry). Kept `help_in_flight` so the
  // open-loop guard still HOLDS on it (fail closed against a double send), but NOT a contact:
  // `reachedCustomer: false`, so no cooldown window, contact-capture recency or "Sent" line is
  // claimed about a message nobody can say was sent. A MISSING status is different — a legacy
  // row or a compact projection — and stays a contact, exactly as `reachedCustomer()` in
  // outcome-vocabulary reads it. (For an UNKNOWN string the two disagree: `reachedCustomer()`
  // says true. Unreachable — the status is a zod enum and `updateDecisionStatus` throws.)
  const unrecognisedStatus = status !== null && (!DECISION_STATUSES.includes(status) || status === 'decided');
  return {
    ...common,
    class: 'help_in_flight',
    deferredUntil,
    reachedCustomer: !unrecognisedStatus,
    unrecognisedStatus
  };
}

/**
 * A pre-D2 shadow proposal: shadow mode, no judgment, and `action` either the payload object
 * `logShadowDecision` writes, or ABSENT. Absent covers the one other shape that reaches a
 * reader: a shadow activity entry from a feed that predates the receipt, which never carried
 * `action` and presented every shadow entry as a proposal held for review. A string `action`
 * (`skip`/`engage`) is the gateway's own mirror and goes through the normalizer as usual.
 */
function isLegacyShadowProposal(row) {
  if (row.mode !== 'shadow' || hasJudgment(row)) return false;
  if (row.action === undefined || row.action === null) return true;
  return typeof row.action === 'object' && !Array.isArray(row.action);
}

/** Statuses a deferral can be promoted INTO — a deferred disposition beside one is history. */
const CONTACT_STATUSES = Object.freeze(['dispatched', 'executed', 'delivered', 'pending_delivery']);

/**
 * `countsTowardRestraint` asks about the JUDGMENT. A help whose composer later failed was
 * still judged `help`; the vocabulary's predicate already excludes only a failure that
 * stands in place of a judgment, so this delegates rather than re-deriving.
 */
function restraintEligible(row) {
  return countsTowardRestraint(row);
}

/**
 * True when a help still OCCUPIES the occasion — sent, on its way, scheduled, or waiting on
 * the merchant's approval. A second send for the same occasion would be a double send. The
 * open-loop volume guard reads this; everything else (blocked, failed, withdrawn, wait) frees
 * the occasion, because nothing reached the shopper and nothing still might.
 *
 * @param {object} classification - the result of `classifyDecision`
 */
function occupiesOccasion(classification) {
  if (!classification) return false;
  const cls = classification.class;
  return CONTACT_CLASSES.includes(cls) ||
    cls === 'help_deferred' ||
    (cls === 'help_blocked' && classification.pendingApproval === true);
}

/**
 * The receipt a SURFACE carries — the flat, JSON-safe projection of a classification that the
 * activity feed puts on every decision entry and the web app falls back to computing when an
 * older feed sent none. One projection, here, so the Lambda and the browser can never shape
 * the same classification two ways.
 *
 * Carries no free text: `failure` is reduced to its phase, and nothing from `reasoning` or a
 * failure message rides along.
 *
 * @param {object} classification - the result of `classifyDecision`
 * @returns {{
 *   class: string,
 *   reasonCode: string|null,
 *   source: string|null,
 *   until: string|null,
 *   deferredUntil: string|null,
 *   failurePhase: string|null,
 *   pendingApproval: boolean,
 *   draftFailed: boolean,
 *   unrecognisedStatus: boolean,
 *   legacy: boolean,
 *   showsReasoning: boolean
 * }}
 */
function toReceipt(classification) {
  const c = classification || classifyDecision(null);
  return {
    class: c.class,
    reasonCode: c.reasonCode || null,
    source: (c.disposition && c.disposition.source) || null,
    until: c.until || null,
    deferredUntil: c.deferredUntil || null,
    failurePhase: (c.failure && c.failure.phase) || null,
    pendingApproval: c.pendingApproval === true,
    draftFailed: c.draftFailed === true,
    unrecognisedStatus: c.unrecognisedStatus === true,
    legacy: c.legacy === true,
    showsReasoning: showsReasoning(c)
  };
}

/**
 * May a surface show this decision's `reasoning` to a merchant? THE rule — the feed, the
 * campaign detail, the web drawer and `summarizeReceipt` all read it, so none can leak what
 * another withholds.
 *
 * The problem it solves: `reasoning` is not always the agent's. A decision failure writes its
 * ERROR as the row's reasoning ("Error getting agent decision: ThrottlingException…",
 * "Failed to parse agent response: …"), and a pre-D2 skip's reasoning cannot be told apart
 * from one. So:
 *
 *   - decision_failed, legacy_ambiguous → never.
 *   - execution_failed → only on a canonical row, and only the JUDGMENT's own reasoning (see
 *     `reasoningFor`): the help was judged, then broke; the judgment's words are the agent's,
 *     and a legacy failed row carries no judgment to take them from.
 *   - every other class carries a judgment → yes.
 *
 * @param {object} classification - the result of `classifyDecision` (or a receipt)
 * @returns {boolean}
 */
function showsReasoning(classification) {
  if (!classification) return false;
  const cls = classification.class;
  if (cls === 'decision_failed' || cls === 'legacy_ambiguous') return false;
  if (cls === 'execution_failed') return classification.legacy !== true;
  return DECISION_CLASSES.includes(cls);
}

/**
 * The reasoning a surface may show for this row, or null. See `showsReasoning`.
 *
 * @param {object} row - a decision row
 * @param {object} [classification] - `classifyDecision(row)`, when the caller already has it
 * @returns {string|null}
 */
function reasoningFor(row, classification) {
  const c = classification || classifyDecision(row);
  if (!showsReasoning(c) || !row) return null;
  const judged = hasJudgment(row) ? row.judgment.reasoning : null;
  if (c.class === 'execution_failed') return judged || null;
  return row.reasoning || judged || null;
}

/** Does this row count as a contact? Read through the classifier, never the action. */
function isContact(row) {
  return classifyDecision(row).reachedCustomer === true;
}

// ── Gateway responses ──────────────────────────────────────────────────────────────────

/**
 * Verdicts for one gateway invocation (the simulator's view, PR 3).
 *
 * `error` is a handler crash — neither a failure of judgment nor restraint. `lost_write` is a
 * decision whose row the caller looked for and did not find: a judgment may have been made,
 * but nobody can read it.
 */
const GATEWAY_VERDICTS = Object.freeze([
  'help', 'wait', 'escalate', 'blocked_before_judgment', 'ineligible',
  'decision_failed', 'lost_write', 'execution_failed', 'error'
]);

/** Gateway response statuses that mean "a help reached an executor" — see classifyGatewayResponse. */
const POST_EXECUTOR_STATUSES = Object.freeze(['executed', 'scheduled', 'suppressed', 'delivery_failed']);

const HELP_CLASSES = Object.freeze([
  'help_blocked', 'help_withdrawn', 'help_deferred', 'help_shadowed', 'help_executed', 'help_in_flight'
]);

/**
 * Classify a gateway response, optionally against the row it wrote.
 *
 * @param {object} res - the gateway's response
 * @param {object} [opts]
 * @param {object|null} [opts.row] - the row read back. Pass `row: null` explicitly to say
 *   "looked it up and it is not there" — that is what makes a lost write a verdict.
 * @param {string} [opts.decisionId] - the decision id, when the response does not carry one
 * @returns {{verdict: string, class: string|null, reasonCode: string|null}}
 */
function classifyGatewayResponse(res, opts = {}) {
  const decisionId = opts.decisionId || (res && res.decisionId) || null;
  if (!res || typeof res !== 'object' || res.status === 'error') {
    return { verdict: 'error', class: null, reasonCode: null };
  }

  const eligibility = res.eligibility;
  if (eligibility && eligibility.kind === 'ineligible') {
    return { verdict: 'ineligible', class: null, reasonCode: eligibility.reasonCode || null };
  }
  if (eligibility && eligibility.kind === 'blocked_before_judgment') {
    return { verdict: 'blocked_before_judgment', class: null, reasonCode: eligibility.reasonCode || null };
  }

  if (res.decisionFailure && res.decisionFailure.stage === 'persistence') {
    return { verdict: 'lost_write', class: null, reasonCode: res.decisionFailure.code || null };
  }

  const rowGiven = Object.prototype.hasOwnProperty.call(opts, 'row');
  if (rowGiven && !opts.row && decisionId) {
    return { verdict: 'lost_write', class: null, reasonCode: DECISION_FAILURE_CODES.DECISION_WRITE_LOST };
  }

  if (opts.row) {
    const c = classifyDecision(opts.row);
    if (c.class === 'decision_failed') return { verdict: 'decision_failed', class: c.class, reasonCode: c.failure.code };
    if (c.class === 'wait' || c.class === 'escalate') return { verdict: c.class, class: c.class, reasonCode: null };
    if (c.class === 'execution_failed') return { verdict: 'execution_failed', class: c.class, reasonCode: c.failure.code };
    if (HELP_CLASSES.includes(c.class)) return { verdict: 'help', class: c.class, reasonCode: c.reasonCode };
    // legacy_ambiguous: the gateway writes canonical rows, so fall through to the response.
  }

  if (res.decisionFailure && !res.judgment) {
    return { verdict: 'decision_failed', class: null, reasonCode: res.decisionFailure.code || null };
  }
  if (res.judgment === 'wait') return { verdict: 'wait', class: null, reasonCode: null };
  if (res.judgment === 'escalate') return { verdict: 'escalate', class: null, reasonCode: null };
  if (res.judgment === 'help') {
    return {
      verdict: 'help',
      class: null,
      reasonCode: (res.disposition && res.disposition.reasonCode) || null
    };
  }

  // Every post-judgment status a gateway handler answers with once a help reached an executor.
  // `delivery_failed` is the five socket handlers' name for `result.success === false`
  // (`status: result.success ? 'executed' : 'delivery_failed'`) — it resolves from `result`
  // exactly as `executed` does: a withhold is still a help, only a hard failure is not.
  if (POST_EXECUTOR_STATUSES.includes(res.status)) {
    const r = res.result;
    const hardFailure = r && r.success === false &&
      !r.skipped && !r.suppressed && !r.held && !r.deferred && !r.shadowed && !r.rowFinalized;
    if (hardFailure || (r && r.decisionFailure)) {
      return {
        verdict: 'execution_failed',
        class: null,
        reasonCode: (r.decisionFailure && r.decisionFailure.code) || null
      };
    }
    return { verdict: 'help', class: null, reasonCode: (r && r.reason) || null };
  }

  // An answer this function does not recognise is reported as an error rather than guessed
  // into restraint.
  return { verdict: 'error', class: null, reasonCode: null };
}

// ── Merchant-facing words ──────────────────────────────────────────────────────────────

/**
 * One phrase per blocked-disposition reason. Completes the sentence
 * "Wanted to {help}, but …". Every `BLOCKED_REASONS` value has its own entry — a test
 * asserts none of them falls through to the source fallback.
 */
const HOLD_PHRASES = Object.freeze({
  // governance (BLOCK_REASONS)
  [BLOCK_REASONS.BLACKOUT]: 'held by a blackout window',
  [BLOCK_REASONS.QUIET_HOURS]: 'held by quiet hours',
  [BLOCK_REASONS.COOLDOWN]: 'held by the cooldown',
  [BLOCK_REASONS.FREQUENCY_CAP_WEEK]: 'held by the weekly frequency cap',
  [BLOCK_REASONS.FREQUENCY_CAP_MONTH]: 'held by the monthly frequency cap',
  [BLOCK_REASONS.FATIGUE]: 'held back because they have been ignoring this channel',
  [BLOCK_REASONS.SUPPRESSED]: 'held because they are unsubscribed from this channel',
  [BLOCK_REASONS.SUBJECT_ERASED]: 'held because their data was erased',
  [BLOCK_REASONS.ENGAGEMENT_HISTORY_READ_FAILED]: 'held because we could not confirm their contact history',
  [BLOCK_REASONS.SUPPRESSION_CHECK_FAILED]: 'held because we could not confirm they can be contacted',
  // feasibility
  [FEASIBILITY_REASONS.CONTACT_CAPTURE_UNUSABLE]: 'the sign-up form could not be shown here',
  [FEASIBILITY_REASONS.NO_CONTACT_AVAILABLE]: 'there was no way to reach them',
  [FEASIBILITY_REASONS.UNKNOWN_CHANNEL]: 'the channel was not one we can send on',
  [FEASIBILITY_REASONS.NO_POSTAL_ADDRESS]: 'held because your store has no postal address for the email footer',
  [FEASIBILITY_REASONS.NO_UNSUBSCRIBE_URL]: 'held because we could not build an unsubscribe link',
  [FEASIBILITY_REASONS.NO_EMAIL_TEMPLATE]: 'there is no email for this kind of moment yet',
  [FEASIBILITY_REASONS.EMAIL_INTENT_UNROUTABLE]: 'the email it meant to write needs details this moment does not carry',
  [FEASIBILITY_REASONS.EMAIL_PAYLOAD_INCOMPLETE]: 'the moment arrived without the details the email needs',
  [FEASIBILITY_REASONS.NO_FEATURABLE_PRODUCTS]: 'none of the products they looked at can be shown any more',
  [FEASIBILITY_REASONS.HOURLY_SEND_CAP]: 'held because your store reached its hourly email limit',
  [FEASIBILITY_REASONS.OFFER_NOT_ISSUED]: 'held because the discount it offered could not be issued',
  [FEASIBILITY_REASONS.OFFER_COPY_UNBACKED]: 'held because the text named a discount we could not issue as written',
  [FEASIBILITY_REASONS.NO_DOMAIN_CONFIGURED]: 'held because your store has no email sender configured',
  [FEASIBILITY_REASONS.DOMAIN_NOT_VERIFIED]: 'held because your store\'s email sender is not verified',
  [FEASIBILITY_REASONS.DOMAIN_EXPIRED]: 'held because your store\'s email sender verification has expired',
  [FEASIBILITY_REASONS.SIMULATOR_RECIPIENT_BLOCKED]: 'held because this test recipient is not allowlisted',
  // executor self-restraint
  [SELF_RESTRAINT_REASONS.ALREADY_FULLY_OPTED_IN]: 'they are already subscribed on every channel',
  [SELF_RESTRAINT_REASONS.ALREADY_OFFERED_THIS_SESSION]: 'they had already been asked this visit',
  // lifecycle
  [LIFECYCLE_REASONS.SCHEDULED]: 'it was scheduled for later',
  [LIFECYCLE_REASONS.ENQUEUED]: 'it was queued to send',
  [LIFECYCLE_REASONS.ESCALATION_PENDING]: 'waiting on your approval',
  [LIFECYCLE_REASONS.GOVERNANCE_BLOCKED]: 'held by your contact rules',
  [LIFECYCLE_REASONS.CONTEXT_STALE]: 'what it was about had changed by send time',
  [LIFECYCLE_REASONS.SELECTION_UNAVAILABLE]: 'the products it chose had changed by send time',
  // authority
  [AUTHORITY_REASONS.ABOVE_RUNG]: 'it needs more autonomy than you have granted',
  [AUTHORITY_REASONS.CLASS_OFF]: 'you have switched that kind of action off',
  [AUTHORITY_REASONS.CONFIG_UNAVAILABLE]: 'we could not read your authority settings, so we held it',
  [AUTHORITY_REASONS.ABOVE_CEILING]: 'the offer was above your discount ceiling',
  // mode (F-24)
  [MODE_REASONS.WORKSPACE_DISABLED]: 'this workspace is disabled, so nothing goes out',
  [MODE_REASONS.WORKSPACE_SHADOWED]: 'that channel was in Shadow by the time it was due to send',
  [MODE_REASONS.WORKSPACE_UNREADABLE]: 'held because we could not read your workspace settings',
  [MODE_REASONS.WORKSPACE_DELETED]: 'the workspace had been deleted, so nothing goes out',
  [MODE_REASONS.OCCASION_RELEASE_DARK]: 'this kind of message is not switched on for live sending yet',
  // deduplication
  [DEDUPLICATION_REASONS.DUPLICATE_EXECUTION]: 'it had already been sent',
  [DEDUPLICATION_REASONS.SHOPIFY_SIDE_EFFECT_FENCE]: 'it had already been done'
});

const SOURCE_PHRASES = Object.freeze({
  governance: 'held by your contact rules',
  authority: 'held by your authority settings',
  feasibility: 'it could not be carried on that channel',
  scheduler: 'it was rescheduled',
  revalidation: 'what it was about had changed by send time',
  mode: 'held by this workspace\'s mode'
});

/**
 * The hold, in the merchant's words. Never returns an empty string.
 *
 * @param {object|null} disposition - `{kind, source, reasonCode}`
 * @returns {string}
 */
function describeHold(disposition) {
  const reason = disposition && disposition.reasonCode;
  // Nobody recorded a cause — a legacy `engage/suppressed` with no `suppressionReason` is
  // normalized to reason `legacy_ambiguous`, source `governance`. Naming "your contact rules"
  // for it would state a cause the row never carried.
  if (!reason || reason === LEGACY_AMBIGUOUS) return UNRECORDED_HOLD;
  if (Object.prototype.hasOwnProperty.call(HOLD_PHRASES, reason)) return HOLD_PHRASES[reason];
  const source = disposition && disposition.source;
  if (source && Object.prototype.hasOwnProperty.call(SOURCE_PHRASES, source)) return SOURCE_PHRASES[source];
  return 'it was held';
}

/** The hold phrase when no reason was recorded. */
const UNRECORDED_HOLD = 'held back (reason not recorded)';

function isoMinute(value) {
  if (!value) return null;
  const t = new Date(value);
  if (isNaN(t.getTime())) return null;
  return `${t.toISOString().slice(0, 16).replace('T', ' ')} UTC`;
}

/**
 * A plain-text one-line receipt for a decision row. Never prints failure text: a failed
 * row's `reasoning` holds an error message, and the merchant is owed "a fault on our side",
 * not a stack trace.
 *
 * @param {object} row - a decision row
 * @param {object} [opts]
 * @param {number} [opts.reasoningChars=0] - append up to this many characters of the AGENT's
 *   reasoning — only for classes that carry a judgment, never for failures or legacy rows.
 * @returns {string}
 */
/**
 * "an email message", "an SMS message", "a chat message" — or plain "a message" when the row
 * names no channel (a contact-capture ask, a pre-D2 shadow row, any non-help), which used to
 * read "a message message". The article follows the SOUND: "SMS" is said "ess-em-ess".
 */
function messagePhrase(channel) {
  if (!channel || typeof channel !== 'string') return 'a message';
  const label = channel === 'sms' ? 'SMS' : channel.replace(/_/g, ' ');
  const article = /^[aeiou]/i.test(label) || label === 'SMS' ? 'an' : 'a';
  return `${article} ${label} message`;
}

function summarizeReceipt(row, { reasoningChars = 0 } = {}) {
  const c = classifyDecision(row);
  const trigger = (row && row.trigger) || 'customer';
  const what = `${messagePhrase(row && row.channel)} (${trigger})`;
  let line;
  switch (c.class) {
    case 'help_executed': line = `Sent ${what}`; break;
    case 'help_in_flight':
      line = c.unrecognisedStatus ? `Planned ${what} — outcome not recorded` : `Sending ${what}`;
      break;
    case 'help_deferred': {
      const until = isoMinute(c.until);
      line = until ? `Planned ${what} — deferred until ${until}` : `Planned ${what} — deferred`;
      break;
    }
    case 'help_blocked': line = `Wanted to send ${what}, but ${describeHold(c.disposition || { reasonCode: c.reasonCode })}`; break;
    case 'help_withdrawn': line = `Planned ${what} — withdrawn before sending`; break;
    case 'help_shadowed': line = `Would have sent ${what}`; break;
    case 'execution_failed': line = `Tried to send ${what}, but it didn't go through`; break;
    case 'wait': line = `Chose to wait (${trigger})`; break;
    case 'escalate': line = `Wanted your input (${trigger}) — nothing was sent`; break;
    case 'decision_failed': line = `Couldn't make a call (${trigger}) — a fault on our side`; break;
    default: line = `Did not send (${trigger}) — recorded before reasons were kept apart`;
  }
  // MAR-411: what the help selected, or why execution withheld it — per product. Only a help
  // carries a selection; a wait proposed nothing.
  const selection = c.class && c.class.startsWith('help_') ? selectionClauseOf(row) : null;
  if (selection) line += ` — ${selection}`;
  const reasoning = reasoningChars > 0 ? reasoningFor(row, c) : null;
  if (reasoning) {
    line += ` — ${String(reasoning).slice(0, reasoningChars)}`;
  }
  return line;
}

/** The selection clause (`@marva/shared/selection-receipt`, dependency-free). */
function selectionClauseOf(row) {
  return describeSelection(row);
}

// ── Measures ───────────────────────────────────────────────────────────────────────────

const MEASURE_KEYS = Object.freeze([
  'attempted', // a decision attempt reached the logger (row written, or its write lost)
  'judged', // … and carried a judgment
  'failed', // … and did not (decision failure, or the write was lost)
  'restraintHelp', // a judged help that enters the restraint denominator
  'restraintWait', // a judged wait that enters it
  'ineligible', // the gateway found no decision-worthy moment
  'policyBlockedBeforeJudgment', // policy stopped it before the model
  'governanceBlocked', // a help the governance enforcer withheld (disposition blocked/governance)
  'executionFailed', // a help that broke on its way to the shopper
  'gatewayError' // the handler crashed
]);

function zeroMeasures() {
  const m = {};
  for (const k of MEASURE_KEYS) m[k] = 0;
  return m;
}

function looksLikeRow(input) {
  return Boolean(input && (input.pk || input.sk || input.createdAt ||
    (input.judgment && typeof input.judgment === 'object')));
}

/**
 * What ONE row — or one gateway response with no row — contributes to the three measures.
 * The gateway's EMF lines and the eval report both read through this, so the dashboards and
 * the eval can never count the same decision two ways.
 *
 * @param {object} input - a decision row, or a gateway response
 * @param {object} [opts]
 * @param {'row'|'response'} [opts.as] - force the reading; inferred otherwise
 * @returns {Record<string, 0|1>}
 */
function measureContributions(input, { as } = {}) {
  const m = zeroMeasures();
  const kind = as || (looksLikeRow(input) ? 'row' : 'response');

  if (kind === 'response') {
    const { verdict } = classifyGatewayResponse(input);
    if (verdict === 'ineligible') m.ineligible = 1;
    else if (verdict === 'blocked_before_judgment') m.policyBlockedBeforeJudgment = 1;
    else if (verdict === 'error') m.gatewayError = 1;
    else if (verdict === 'lost_write') { m.attempted = 1; m.failed = 1; }
    // Every other verdict wrote a row; its measures come from that row, never twice.
    return m;
  }

  // An eligibility short of `eligible` never makes a row today; guard it anyway so a future
  // writer cannot put a pre-judgment block into the attempt denominator.
  if (input && input.eligibility && input.eligibility.kind && input.eligibility.kind !== 'eligible') {
    if (input.eligibility.kind === 'ineligible') m.ineligible = 1;
    else m.policyBlockedBeforeJudgment = 1;
    return m;
  }

  const c = classifyDecision(input);
  m.attempted = 1;
  if (c.class === 'decision_failed') {
    m.failed = 1;
    return m;
  }
  if (c.class === 'legacy_ambiguous') return m; // attempted, but no readable judgment
  m.judged = 1;
  if (c.countsTowardRestraint && c.judgment === 'help') m.restraintHelp = 1;
  if (c.countsTowardRestraint && c.judgment === 'wait') m.restraintWait = 1;
  if (c.class === 'help_blocked' && c.disposition && c.disposition.kind === 'blocked' &&
    c.disposition.source === 'governance') {
    m.governanceBlocked = 1;
  }
  if (c.class === 'execution_failed') m.executionFailed = 1;
  return m;
}

function ratio(numerator, denominator) {
  return denominator > 0 ? numerator / denominator : null;
}

/**
 * The response a LOST decision write is counted as — the one shape, so the gateway's
 * `recordLostWrite` and any offline reader (the eval, which learns of a lost write by looking
 * the row up) can never count it two ways. It is an attempt and a reliability failure, and its
 * judgment (if any) is unreadable, so it enters neither restraint arm.
 *
 * @param {{decisionId?: string|null}} [args]
 * @returns {object} a gateway-response-shaped input for `measureContributions(…, {as:'response'})`
 */
function lostWriteMeasureInput({ decisionId = null } = {}) {
  return {
    status: 'skipped',
    decisionId,
    decisionFailure: { stage: 'persistence', code: DECISION_FAILURE_CODES.DECISION_WRITE_LOST }
  };
}

/**
 * Who was asked to judge this attempt — the `judgmentSource` a decision attempt is sliced by.
 * A failure has no `judgment.source`, so it is attributed to the path that attempted it: the
 * caller's declared source, else `agent` (every other path exists to ask the model). Without
 * that, agent-sliced reliability would be 1.0 by construction — every agent failure would leave
 * the agent denominator.
 *
 * @param {object|null} input - a decision row (or a response, which carries no judgment)
 * @param {string|null} [declared] - the decision's own declared `judgmentSource`
 * @returns {string}
 */
function attemptSource(input, declared = null) {
  const own = input && input.judgment && typeof input.judgment === 'object' ? input.judgment.source : null;
  return own || declared || 'agent';
}

/** Is this list item a `{input, as, declaredSource?}` entry rather than a bare row/response? */
function isMeasureEntry(item) {
  return Boolean(item && typeof item === 'object' &&
    Object.prototype.hasOwnProperty.call(item, 'input') &&
    (item.as === 'row' || item.as === 'response'));
}

/**
 * The three measures over a population of rows and/or responses.
 *
 *   restraint         = restraintWait / (restraintHelp + restraintWait)
 *   reliability       = judged / attempted
 *   agentReliability  = judged / attempted, over attempts whose `attemptSource` is `agent` — the
 *                       one to read: deterministic contracts (the chat fast path, comparison)
 *                       synthesise a help with no model call and cannot fail at model/parse, so
 *                       the unsliced ratio looks near-perfect whatever Bedrock does
 *   governanceLoad    = (policyBlockedBeforeJudgment + governanceBlocked)
 *                       / (policyBlockedBeforeJudgment + attempted)
 *
 * A ratio with an empty denominator is `null` — never 0 and never 1, both of which are claims.
 *
 * Items may be bare rows/responses (read with `opts.as`, or inferred), or — for a population that
 * MIXES the two, where inference cannot be trusted — `{input, as, declaredSource?}` entries, each
 * read as it says.
 *
 * @param {Array<object>} list
 * @param {object} [opts] - forwarded to `measureContributions` for bare items
 * @returns {{
 *   counts: Record<string, number>,
 *   agentCounts: {attempted: number, judged: number},
 *   restraint: number|null, reliability: number|null, agentReliability: number|null,
 *   governanceLoad: number|null,
 *   ratios: Record<'restraint'|'reliability'|'agentReliability'|'governanceLoad',
 *     {value: number|null, numerator: number, denominator: number}>
 * }}
 */
function computeDecisionMeasures(list, opts = {}) {
  const counts = zeroMeasures();
  const agentCounts = { attempted: 0, judged: 0 };
  for (const item of list || []) {
    const entry = isMeasureEntry(item) ? item : { input: item, as: opts.as, declaredSource: null };
    const m = measureContributions(entry.input, entry.as ? { as: entry.as } : {});
    for (const k of MEASURE_KEYS) counts[k] += m[k];
    if (m.attempted && attemptSource(entry.as === 'response' ? null : entry.input, entry.declaredSource) === 'agent') {
      agentCounts.attempted += m.attempted;
      agentCounts.judged += m.judged;
    }
  }
  const r = (numerator, denominator) => ({ value: ratio(numerator, denominator), numerator, denominator });
  const ratios = {
    restraint: r(counts.restraintWait, counts.restraintHelp + counts.restraintWait),
    reliability: r(counts.judged, counts.attempted),
    agentReliability: r(agentCounts.judged, agentCounts.attempted),
    governanceLoad: r(
      counts.policyBlockedBeforeJudgment + counts.governanceBlocked,
      counts.policyBlockedBeforeJudgment + counts.attempted
    )
  };
  return {
    counts,
    agentCounts,
    restraint: ratios.restraint.value,
    reliability: ratios.reliability.value,
    agentReliability: ratios.agentReliability.value,
    governanceLoad: ratios.governanceLoad.value,
    ratios
  };
}

/** The reason codes a pre-judgment exit may carry — the bound on the EMF `reasonCode` dim. */
const PRE_JUDGMENT_REASON_CODES = Object.freeze([
  ...Object.values(INELIGIBLE_REASONS),
  ...Object.values(PRE_JUDGMENT_BLOCK_REASONS)
]);

module.exports = {
  DECISION_STATUSES,
  DECISION_CLASSES,
  CONTACT_CLASSES,
  HELP_CLASSES,
  EXECUTION_FAILURE_PHASES,
  GATEWAY_VERDICTS,
  MEASURE_KEYS,
  PRE_JUDGMENT_REASON_CODES,
  HOLD_PHRASES,
  classifyDecision,
  classifyGatewayResponse,
  toReceipt,
  showsReasoning,
  reasoningFor,
  executionFailurePhase,
  occupiesOccasion,
  isContact,
  describeHold,
  summarizeReceipt,
  measureContributions,
  computeDecisionMeasures,
  lostWriteMeasureInput,
  attemptSource,
  // re-exported for consumers that only need the list of blocked reasons alongside the phrases
  BLOCKED_REASONS
};
